import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

import * as filterHelpers from 'app/modules/browse/helpers/filter.helpers';
import {
  BrowseFilter,
  FilterNamesEnum,
} from 'app/modules/browse/models/filter';
import { FilterOption } from 'app/modules/browse/models/filter-options';
import { Sport } from 'app/modules/browse/models/sport';
import { FilterService } from 'app/modules/browse/services/filter.service';
import { TranslateModule } from '@ngx-translate/core';
import { FilterSliderComponent } from '../../filter-slider/filter-slider.component';
import { NgTemplateOutlet } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { selectMeUnit } from 'app/store/me/me.selectors';
import { Unit } from '@kinomap-dev/ng-ui-lib';

@Component({
  selector: 'app-filter-box',
  templateUrl: './filter-box.component.html',
  styleUrls: ['./filter-box.component.scss'],
  standalone: true,
  imports: [FilterSliderComponent, TranslateModule, NgTemplateOutlet],
})
export class FilterBoxComponent {
  private el: ElementRef = inject(ElementRef);
  private renderer: Renderer2 = inject(Renderer2);
  private filterService: FilterService = inject(FilterService);

  @Input() filter!: BrowseFilter;
  @Input() sport: Sport;
  @Input() unit: Unit = 'metric';

  @Output() closeBox: EventEmitter<void> = new EventEmitter<void>();
  @Output() applyFilter: EventEmitter<FilterOption[]> = new EventEmitter<
    FilterOption[]
  >();

  filterHelpers = filterHelpers;
  filterOptions: FilterOption[] = [];
  selectedOptions: FilterOption[] = [];
  isFilterEnabled: boolean = true;
  customSelectedOptions: FilterOption[] = [];
  private customFilterOption?: FilterOption;

  private isFirstClick: boolean = true;
  private listenerFn!: () => void;

  get isSlider(): boolean {
    const sliderFilterNames: FilterNamesEnum[] =
      filterHelpers.getFilterNamesWithSlider();
    return sliderFilterNames.includes(this.filter.name);
  }

  get hasCustomSliderOption(): boolean {
    if (!this.filter) {
      return false;
    }
    return filterHelpers.filterHasCustomSliderOption(this.filter.name);
  }

  isCustomOption(option: FilterOption): boolean {
    return filterHelpers.isCustomFilterOption(option);
  }

  onApplyFilter(): void {
    if (this.isFilterEnabled) {
      this.applyFilter.emit(this.selectedOptions);
    }
  }

  onClearFilter(): void {
    this.selectedOptions = filterHelpers.getDefaultSelectedOptions(
      this.filter.name
    );
    this.initializeCustomSliderState();
    this.updateFilterEnabling();
  }

  onCheckboxChange(event: any, option: FilterOption): void {
    const isCustom: boolean = this.isCustomOption(option);
    const isAlreadySelected: boolean = filterHelpers.isOptionSelected(
      option,
      this.selectedOptions
    );

    if (
      event.target.checked &&
      !isAlreadySelected
    ) {
      if (isCustom) {
        const optionToAdd: FilterOption | null =
          this.getCurrentCustomSelection();
        this.selectedOptions = optionToAdd ? [optionToAdd] : [];
      } else {
        this.removeCustomSelections();
        this.selectedOptions.push(option);
      }

      if (
        this.filterOptions.length > 1 &&
        this.selectedOptions.length === this.filterOptions.length &&
        !filterHelpers.canFilterOptionsAllBeSelected(this.filter.name)
      ) {
        event.target.checked = false;
        this.selectedOptions = [];
      }
    } else if (!event.target.checked) {
      if (isCustom) {
        this.selectedOptions = this.selectedOptions.filter(
          (selectedOption: FilterOption) => {
            return !filterHelpers.isCustomSelectionForFilter(
              selectedOption,
              this.filter.name,
              this.filterOptions
            );
          }
        );
      } else {
        this.selectedOptions = this.selectedOptions.filter(
          (o: FilterOption) => o.value !== option.value
        );
      }
    }

    this.updateFilterEnabling();
  }

  onRadioChange(option: FilterOption): void {
    this.selectedOptions = [option];
    this.updateFilterEnabling();
  }

  onSliderChange(): void {
    if (this.hasCustomSliderOption) {
      this.customSelectedOptions = this.customSelectedOptions.map(
        (option: FilterOption) => {
          return { ...option, isCustom: true };
        }
      );

      const currentCustomSelection: FilterOption | null =
        this.getCurrentCustomSelection();

      if (currentCustomSelection) {
        const hasCustomSelected: boolean = this.selectedOptions.some(
          (option: FilterOption) => {
            return filterHelpers.isCustomSelectionForFilter(
              option,
              this.filter.name,
              this.filterOptions
            );
          }
        );

        if (hasCustomSelected) {
          this.selectedOptions = [{ ...currentCustomSelection }];
        }
      }
    }

    this.updateFilterEnabling();
  }

  onClose(): void {
    this.closeBox.emit();
  }

  private closeWhenClickingOutside(): void {
    this.listenerFn = this.renderer.listen(
      'window',
      'click',
      (event: Event) => {
        if (this.isFirstClick) {
          this.isFirstClick = false;
          return;
        }

        const clikedOutside: boolean = !this.el.nativeElement.contains(
          event.target
        );

        if (clikedOutside) {
          this.onClose();
        }
      }
    );
  }

  private updateFilterEnabling(): void {
    this.isFilterEnabled = filterHelpers.haveOptionsChanged(
      this.filter,
      this.selectedOptions
    );
  }

  private initializeCustomSliderState(): void {
    if (!this.hasCustomSliderOption) {
      this.customFilterOption = undefined;
      this.customSelectedOptions = [];
      return;
    }

    this.customFilterOption =
      this.filterOptions.find((option: FilterOption) => {
        return this.isCustomOption(option);
      }) ??
      filterHelpers.getCustomSliderDefaultOption(this.filter.name);

    const existingCustomSelection: FilterOption | undefined =
      this.selectedOptions.find((option: FilterOption) => {
        return filterHelpers.isCustomSelectionForFilter(
          option,
          this.filter.name,
          this.filterOptions
        );
      });

    const baseSelection: FilterOption | undefined =
      existingCustomSelection ?? this.customFilterOption;

    this.customSelectedOptions = baseSelection
      ? [{ ...baseSelection, isCustom: true }]
      : [];

    if (existingCustomSelection) {
      const customSelection: FilterOption = {
        ...this.customSelectedOptions[0],
      };
      this.selectedOptions = [customSelection];
    }
  }

  private getCurrentCustomSelection(): FilterOption | null {
    if (!this.hasCustomSliderOption || !this.customSelectedOptions.length) {
      return null;
    }
    return { ...this.customSelectedOptions[0], isCustom: true };
  }

  private removeCustomSelections(): void {
    this.selectedOptions = this.selectedOptions.filter(
      (option: FilterOption) => {
        return !filterHelpers.isCustomSelectionForFilter(
          option,
          this.filter.name,
          this.filterOptions
        );
      }
    );
  }

  ngOnInit(): void {
    this.closeWhenClickingOutside();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !!changes.filter ||
      (!!changes.sport && this.filter.name === FilterNamesEnum.WorkoutType)
    ) {
      this.filterOptions = this.filterService.getFilterOptions(
        this.filter,
        this.sport
      );
      this.selectedOptions = [...this.filter.selectedOptions];
      this.initializeCustomSliderState();
      this.updateFilterEnabling();
    }
  }

  ngOnDestroy(): void {
    if (this.listenerFn) {
      this.listenerFn();
    }
  }
}
